<script setup>
import { defineProps, defineEmits, ref, computed } from 'vue'
import User from '../../../api/User'
import { useMainStore } from '@/stores/main'
const mainStore = useMainStore()

const processing = ref(false)
const created = ref(false)
const failed = ref(false)
const key_value = ref("")

const key_name = ref("")
const key_description = ref("")

// Form validation
const isFormValid = computed(() => {
  const nameValue = (key_name.value || '').trim()
  const descriptionValue = (key_description.value || '').trim()

  return nameValue !== '' && descriptionValue !== ''
})

async function submit() {
  processing.value = true
  created.value = false
  failed.value = false

  let data = await User.create_api_key(key_name.value, key_description.value)
  if (data.status == "success") {
    created.value = true
    key_value.value = data.key.Key

    mainStore.event_emit("created_apikey")
  } else {
    failed.value = true
  }

  processing.value = false
}

function copyToClipboard() {
  navigator.clipboard.writeText(key_value.value)
}
</script>

<template>
  <div class="create-apikey-container">
    <!-- Header -->
    <div class="box-header">
      <h3>Create API Key</h3>
    </div>

    <!-- Content -->
    <div class="box-content">
      <!-- Success State -->
      <div v-if="created" class="success-state">
        <div class="success-icon">
          <font-awesome-icon :icon="['fas', 'check-circle']" />
        </div>
        <h4>API Key Created!</h4>
        <p class="success-message">Your API key has been successfully created.</p>

        <div class="api-key-display">
          <label>API Key</label>
          <div class="key-container">
            <code class="key-value">{{ key_value }}</code>
            <button type="button" @click="copyToClipboard" class="copy-btn" title="Copy to clipboard">
              <font-awesome-icon :icon="['fas', 'copy']" />
            </button>
          </div>
          <div class="key-warning">
            <font-awesome-icon :icon="['fas', 'circle-exclamation']" />
            Please save this API key in a secure place. You will not be able to see it again.
          </div>
        </div>
      </div>

      <!-- Form -->
      <form v-else class="apikey-form" @submit.prevent="submit">
        <div class="form-content">
          <!-- Error Message -->
          <div v-if="failed" class="alert alert-error mb-3">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
            <span>Failed to create API key. Please try a different name and description, and ensure they only contain ASCII characters.</span>
          </div>

          <!-- Name -->
          <div class="form-group">
            <label for="keyName">Name *</label>
            <div class="input-wrapper">
              <input
                type="text"
                v-model="key_name"
                id="keyName"
                placeholder="Production API Key"
                :disabled="processing"
                required
              >
            </div>
            <div class="form-help">
              A descriptive name for this API key
            </div>
          </div>

          <!-- Description -->
          <div class="form-group">
            <label for="keyDescription">Description *</label>
            <div class="input-wrapper">
              <textarea
                v-model="key_description"
                id="keyDescription"
                placeholder="Used for production server integration"
                rows="4"
                :disabled="processing"
                required
              ></textarea>
            </div>
            <div class="form-help">
              Provide details about how this key will be used
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="form-actions">
          <button
            type="submit"
            class="submit-btn"
            :disabled="!isFormValid || processing"
          >
            <font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" spin />
            <span v-if="processing">Creating...</span>
            <span v-else>Create API Key</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.create-apikey-container {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  color: white;
  margin: 0;
  padding: 0;
}

.box-header {
  background-color: rgba(255, 255, 255, 0.03);
  padding: 1rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  width: 100%;
  margin: 0;
  border-radius: 10px 0 0 0;
}

.box-header h3 {
  margin: 0;
  color: #E5E7EB;
  font-size: 1.1rem;
  font-weight: 500;
}

.box-content {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
}

/* Success State */
.success-state {
  text-align: center;
  padding: 1rem 0;
}

.success-icon {
  font-size: 3rem;
  color: #10B981;
  margin-bottom: 1rem;
}

.success-state h4 {
  color: white;
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
}

.success-message {
  color: #9CA3AF;
  margin: 0 0 2rem 0;
}

.api-key-display {
  background: rgba(26, 29, 36, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  padding: 1.5rem;
  text-align: left;
}

.api-key-display label {
  display: block;
  margin-bottom: 0.5rem;
  color: #E5E7EB;
  font-weight: 500;
  font-size: 0.875rem;
}

.key-container {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 1rem;
}

.key-value {
  flex: 1;
  background-color: #171C24;
  border: 1px solid #374151;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  color: #10B981;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.875rem;
  word-break: break-all;
}

.copy-btn {
  padding: 0.75rem 1rem;
  background-color: #374151;
  color: #E5E7EB;
  border: 1px solid #4B5563;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.copy-btn:hover {
  background-color: #4B5563;
  color: white;
}

.key-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #FCA5A5;
  font-size: 0.85rem;
  padding: 0.75rem 1rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
}

/* Form Styles */
.apikey-form {
  max-width: none;
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 0;
  padding: 0;
}

.form-content {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #E5E7EB;
  font-weight: 500;
  font-size: 0.875rem;
}

.input-wrapper {
  background-color: #171C24;
  border: 1px solid #374151;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  transition: border-color 0.2s;
}

.input-wrapper:focus-within {
  border-color: #7357D2;
}

.input-wrapper input,
.input-wrapper textarea {
  background: transparent;
  border: none;
  outline: none;
  color: white;
  width: 100%;
  resize: vertical;
}

.input-wrapper input:disabled,
.input-wrapper textarea:disabled {
  color: #9CA3AF;
  cursor: not-allowed;
  opacity: 1;
}

.input-wrapper input::placeholder,
.input-wrapper textarea::placeholder {
  color: #6B7280;
}

.form-help {
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: #6B7280;
}

/* Alert */
.alert {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.alert-error {
  background-color: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #FCA5A5;
}

.mb-3 {
  margin-bottom: 1rem;
}

/* Form Actions */
.form-actions {
  margin-top: 2rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #374151;
  text-align: center;
  background-color: #191f27;
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  box-sizing: border-box;
  margin-left: -2rem;
  margin-right: -2rem;
  margin-bottom: -2rem;
}

.submit-btn {
  padding: 0.75rem 2rem;
  background-color: #7357D2;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
}

.submit-btn:hover:not(:disabled) {
  background-color: #7F62E3;
  transform: translateY(-1px);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Custom scrollbar */
.box-content::-webkit-scrollbar,
.form-content::-webkit-scrollbar {
  width: 6px;
}

.box-content::-webkit-scrollbar-track,
.form-content::-webkit-scrollbar-track {
  background: #1f2937;
}

.box-content::-webkit-scrollbar-thumb,
.form-content::-webkit-scrollbar-thumb {
  background: #374151;
  border-radius: 3px;
}

.box-content::-webkit-scrollbar-thumb:hover,
.form-content::-webkit-scrollbar-thumb:hover {
  background: #4b5563;
}
</style>
